import { debounce } from "@mui/material";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { Autocomplete } from "$components/Forms/AutoComplete";
import useJStyles from "$hooks/useJStyles";
import GenericSelect from "$forms/GenericSelect";
import BataillogDatePicker from "$components/BataillogDatePicker/BataillogDatePicker";
import {
  EnumFilterTypes,
  FilterColumn,
} from "$components/DataTable/DataTable.type";
import {
  useDataDispatch,
  useDataPagination,
} from "$components/DataTable/DataTableContext";
import { InputText } from "$components/Forms/InputText";
import { InputNumber } from "$components/Forms/InputNumber";

export const FilterCell = ({
  name,
  label,
  type,
  options,
  startDate,
  endDate,
  multiple = false,
  ...rest
}: FilterColumn & { name: string }) => {
  const dispatch = useDataDispatch();
  const { filters } = useDataPagination();
  const { jclasses } = useJStyles();
  let displayCell: ReactNode;

  const handleChange = useMemo(
    () =>
      debounce((value) => {
        dispatch({
          type: "changeFilter",
          payload: {
            name,
            value,
          },
        });
      }, 300),
    [dispatch, name]
  );

  // PATCH
  const [startDateState, setStartDate] = useState<Date>();
  const [endDateState, setEndDate] = useState<Date>();
  useEffect(() => {
    setStartDate(startDate);
    setEndDate(endDate);
  }, [startDate, endDate]);
  // END PATCH

  const onDateChange = useCallback(
    (dates: any) => {
      if (Array.isArray(dates)) {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);

        const value: { startDate: any; endDate?: any } = { startDate: start };
        if (end !== null && end !== undefined) {
          value.endDate = end;
        }

        dispatch({
          type: "changeFilter",
          payload: {
            name,
            value,
          },
        });
      }
    },
    [dispatch, name]
  );

  switch (type) {
    case EnumFilterTypes.INPUT:
      displayCell = (
        <InputText
          withControl={false}
          label={label}
          {...rest}
          onChange={(e) => handleChange(e.target.value)}
        />
      );
      break;

    case EnumFilterTypes.INPUT_NUMBER:
      displayCell = (
        <InputNumber
          withControl={false}
          label={label}
          min={0}
          step={1}
          {...rest}
          onChange={(e) => handleChange(e.target.value)}
        />
      );
      break;

    case EnumFilterTypes.AUTOCOMPLETE:
      displayCell = <Autocomplete label={label} {...rest} />;
      break;

    case EnumFilterTypes.SELECT:
      displayCell = (
        <GenericSelect
          multiple={multiple}
          label={label}
          className={jclasses.jTableLabelHeader}
          onChange={handleChange}
          value={filters?.[name]}
          options={options}
          style={{ maxWidth: 250 }}
        />
      );
      break;

    case EnumFilterTypes.DATE:
      displayCell = (
        <BataillogDatePicker
          label={label}
          selectsRange
          startDate={startDateState}
          endDate={endDateState}
          onDateChange={onDateChange}
        />
      );
      break;

    default:
      displayCell = null;
      break;
  }

  return <Box className={jclasses.jTopHead}>{displayCell}</Box>;
};
